// =============================================================================
// FILE: packages/renderer/src/components/public/GlobalIntelligenceReveal.tsx
// PURPOSE: "Global Intelligence Reveal" — the cinematic full-screen experience
//          triggered when the R2 Companion icon is clicked. An 8K-style world
//          map fills the screen with glowing, blinking operational nodes, a
//          live "Global Activity Feed" overlays recent trial signups / project
//          starts / bids, and a scoped Warp Drive starfield powers a premium
//          fade-to-warp entrance. The chat panel slides in as a secondary layer.
// =============================================================================

import { useEffect, useRef, useState, type JSX } from 'react';
import { X, Globe2, Radio } from 'lucide-react';
import { fetchActivity, countryFlag, formatUSD } from '../../lib/auction';

const MAP_SRC = `${import.meta.env.BASE_URL}ops-world-map.png`;

interface OpsNode { city: string; x: number; y: number; tone: 'teal' | 'gold'; }

const NODES: OpsNode[] = [
  { city: 'Calgary',   x: 20, y: 33, tone: 'gold' },
  { city: 'New York',  x: 26, y: 40, tone: 'teal' },
  { city: 'São Paulo', x: 33, y: 72, tone: 'teal' },
  { city: 'London',    x: 47, y: 33, tone: 'teal' },
  { city: 'Dubai',     x: 60, y: 46, tone: 'teal' },
  { city: 'Manila',    x: 80, y: 53, tone: 'gold' },
  { city: 'Singapore', x: 76, y: 60, tone: 'teal' },
  { city: 'Tokyo',     x: 85, y: 40, tone: 'teal' },
  { city: 'Sydney',    x: 87, y: 78, tone: 'teal' },
];

const TONE = {
  teal: { dot: '#5eead4', ring: 'rgba(37,181,168,0.55)' },
  gold: { dot: '#fcd34d', ring: 'rgba(245,158,11,0.55)' },
};

interface FeedItem { id: string; icon: 'user' | 'project' | 'bid' | 'cert'; text: string; }

const SIM_CITIES: Array<{ city: string; country: string }> = [
  { city: 'Tokyo', country: 'JP' }, { city: 'Calgary', country: 'CA' }, { city: 'Dubai', country: 'AE' },
  { city: 'London', country: 'GB' }, { city: 'Singapore', country: 'SG' }, { city: 'Manila', country: 'PH' },
  { city: 'Sydney', country: 'AU' }, { city: 'Dallas', country: 'US' }, { city: 'São Paulo', country: 'BR' },
  { city: 'Toronto', country: 'CA' }, { city: 'Berlin', country: 'DE' }, { city: 'Riyadh', country: 'SA' },
];

function simEvent(): FeedItem {
  const c = SIM_CITIES[Math.floor(Math.random() * SIM_CITIES.length)] ?? SIM_CITIES[0]!;
  const kinds: Array<{ icon: FeedItem['icon']; make: () => string }> = [
    { icon: 'user',    make: () => `New user connected · ${countryFlag(c.country)} ${c.city}` },
    { icon: 'project', make: () => `Project started · ${countryFlag(c.country)} ${c.city}` },
    { icon: 'user',    make: () => `Firm started a trial · ${countryFlag(c.country)} ${c.city}` },
    { icon: 'cert',    make: () => `Certification Pass issued · ${countryFlag(c.country)} ${c.city}` },
    { icon: 'project', make: () => `Engineering package generated · ${countryFlag(c.country)} ${c.city}` },
  ];
  const k = kinds[Math.floor(Math.random() * kinds.length)] ?? kinds[0]!;
  return { id: crypto.randomUUID(), icon: k.icon, text: k.make() };
}

// Scoped warp starfield for the reveal backdrop.
function RevealWarp(): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    interface S { nx: number; ny: number; z: number; size: number; hue: number; }
    const make = (far: boolean): S => { const a = Math.random() * Math.PI * 2; const sp = Math.random() * 0.5 + 0.02; return { nx: Math.cos(a) * sp, ny: Math.sin(a) * sp, z: far ? Math.random() : 0.9 + Math.random() * 0.1, size: Math.random() * 1.6 + 0.5, hue: 170 + Math.random() * 38 }; };
    const stars = Array.from({ length: 200 }, () => make(true));
    let speed = 0.02, raf = 0, alive = true;
    const t0 = performance.now();
    const draw = (): void => {
      if (!alive) return;
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (canvas.width !== w || canvas.height !== h) { canvas.width = Math.max(w, 1); canvas.height = Math.max(h, 1); }
      // Warp bursts on entrance then settles to a gentle drift.
      const elapsed = (performance.now() - t0) / 1000;
      const target = elapsed < 1.1 ? 0.03 : 0.0035;
      speed += (target - speed) * 0.04;
      ctx.fillStyle = 'rgba(4,7,13,0.26)'; ctx.fillRect(0, 0, w, h);
      const cx = w * 0.5, cy = h * 0.5, fov = Math.min(w, h) * 0.6;
      for (const s of stars) {
        s.z -= speed;
        if (s.z <= 0.001) { Object.assign(s, make(false)); continue; }
        const sx = cx + (s.nx / s.z) * fov, sy = cy + (s.ny / s.z) * fov;
        if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) { Object.assign(s, make(false)); continue; }
        const near = Math.pow(1 - s.z, 1.6); const bright = Math.min(1, near * 2.2); const r = Math.max(0.25, s.size * near * 2);
        if (speed > 0.004) {
          const pz = Math.min(1, s.z + speed * 5); const px = cx + (s.nx / pz) * fov, py = cy + (s.ny / pz) * fov;
          const g = ctx.createLinearGradient(px, py, sx, sy);
          g.addColorStop(0, `hsla(${s.hue},90%,70%,0)`); g.addColorStop(1, `hsla(${s.hue},92%,82%,${bright * 0.6})`);
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.strokeStyle = g; ctx.lineWidth = r * 0.5; ctx.stroke();
        }
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${s.hue},92%,88%,${bright})`; ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, []);
  return <canvas ref={ref} aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.5, zIndex: 0 }} />;
}

export function GlobalIntelligenceReveal({ onClose, sidePanelWidth = 0 }: { onClose: () => void; sidePanelWidth?: number }): JSX.Element {
  const [feed, setFeed] = useState<FeedItem[]>(() => Array.from({ length: 4 }, () => simEvent()));

  // Live activity — real auction bids interleaved with simulated network events.
  useEffect(() => {
    let alive = true;
    const push = (item: FeedItem): void => setFeed((cur) => [item, ...cur].slice(0, 7));
    const loadReal = async (): Promise<void> => {
      try {
        const events = await fetchActivity(4);
        if (!alive) return;
        for (const e of events.slice(0, 2)) push({ id: crypto.randomUUID(), icon: 'bid', text: `Legacy License bid · ${countryFlag(e.country)} ${e.country} · ${formatUSD(e.bid_amount)}` });
      } catch { /* simulated only */ }
    };
    void loadReal();
    const sim = setInterval(() => push(simEvent()), 3200);
    const real = setInterval(() => void loadReal(), 20000);
    return () => { alive = false; clearInterval(sim); clearInterval(real); };
  }, []);

  // ESC to dismiss.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const iconFor = (k: FeedItem['icon']): string => (k === 'user' ? '🟢' : k === 'project' ? '🛰️' : k === 'cert' ? '✅' : '💠');

  return (
    <div
      data-testid="global-intel-reveal"
      style={{ position: 'fixed', inset: 0, zIndex: 55, background: '#04070d', animation: 'revealFade 0.6s ease both', overflow: 'hidden' }}
    >
      <style>{`
        @keyframes revealFade { from{opacity:0;} to{opacity:1;} }
        @keyframes revealMapIn { from{opacity:0; transform: scale(1.08);} to{opacity:1; transform: scale(1);} }
        @keyframes feedItemIn { from{opacity:0; transform: translateX(-14px);} to{opacity:1; transform:none;} }
        @keyframes nodePulse { 0%,100%{opacity:0.85;} 50%{opacity:1;} }
      `}</style>

      <RevealWarp />

      {/* 8K-style world map */}
      <div
        data-testid="reveal-map"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          backgroundImage: `url(${MAP_SRC})`, backgroundSize: 'cover', backgroundPosition: 'center',
          animation: 'revealMapIn 0.9s cubic-bezier(0.22,1,0.36,1) both',
          maskImage: 'radial-gradient(ellipse at center, #000 55%, transparent 92%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, #000 55%, transparent 92%)',
        }}
      >
        {NODES.map((n, i) => (
          <div key={n.city} className="absolute" style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-50%,-50%)', animation: `nodePulse 2.4s ease-in-out ${i * 0.12}s infinite` }} data-testid={`reveal-node-${n.city.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="absolute inset-0 -m-3 rounded-full animate-ping" style={{ background: TONE[n.tone].ring, animationDuration: '2.6s' }} />
            <span className="block rounded-full" style={{ width: 12, height: 12, background: TONE[n.tone].dot, boxShadow: `0 0 16px ${TONE[n.tone].dot}` }} />
            <span className="absolute left-1/2 top-full mt-1.5 -translate-x-1/2 text-[10px] tracking-wide whitespace-nowrap" style={{ color: 'rgba(220,235,240,0.7)' }}>{n.city}</span>
          </div>
        ))}
      </div>

      {/* Vignette for depth */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', background: 'radial-gradient(ellipse at center, transparent 30%, rgba(4,7,13,0.55) 100%)' }} />

      {/* Header */}
      <div style={{ position: 'absolute', top: 22, left: 26, zIndex: 4, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Globe2 style={{ width: 20, height: 20, color: '#5eead4' }} strokeWidth={1.7} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#e6fffb', letterSpacing: '0.01em' }}>RCD² Global Intelligence</div>
            <div style={{ fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.6)' }}>Live Engineering Network</div>
          </div>
        </div>
      </div>

      {/* Close */}
      <button type="button" data-testid="reveal-close" onClick={onClose} title="Close (Esc)"
        style={{ position: 'absolute', top: 22, right: sidePanelWidth > 0 ? sidePanelWidth + 26 : 26, zIndex: 70, width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(8,16,28,0.6)', border: '1px solid rgba(120,140,160,0.4)', color: 'rgba(200,215,225,0.85)', cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
        <X style={{ width: 18, height: 18 }} />
      </button>

      {/* Live Global Activity Feed */}
      <div
        data-testid="reveal-live-feed"
        style={{
          position: 'absolute', left: 26, bottom: 26, zIndex: 5,
          width: 'min(340px, calc(100vw - 52px))', maxHeight: '46vh', overflow: 'hidden',
          background: 'rgba(6,12,22,0.66)', border: '1px solid rgba(37,181,168,0.28)', borderRadius: 14,
          backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', padding: '12px 14px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Radio style={{ width: 13, height: 13, color: '#22c55e' }} className="animate-pulse" />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(190,210,215,0.7)' }}>Live Activity Feed</span>
          <span style={{ marginLeft: 'auto', fontSize: 9, color: '#22c55e' }}>● LIVE</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {feed.map((f, i) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, animation: i === 0 ? 'feedItemIn 0.4s ease both' : undefined, opacity: Math.max(0.35, 1 - i * 0.12) }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{iconFor(f.icon)}</span>
              <span style={{ fontSize: 11.5, color: 'rgba(215,232,235,0.9)', fontFamily: 'monospace', lineHeight: 1.4 }}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Dim strip reserved for the chat panel so the map stays readable */}
      {sidePanelWidth > 0 && (
        <div aria-hidden style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: sidePanelWidth, zIndex: 3, pointerEvents: 'none', background: 'linear-gradient(90deg, transparent, rgba(4,7,13,0.4))' }} />
      )}
    </div>
  );
}
